import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Avatar, Group, Menu, Skeleton, Text, Tooltip, UnstyledButton } from '@mantine/core'
import { IconCheck, IconChevronDown, IconLogout, IconUserEdit } from '@tabler/icons-react'
import GoogleIcon from '@legacy/common/Icons/Google'
import { useCurrentUser } from 'context/useCurrentUser'
import { useAvatarOverride } from 'context/useAvatarOverride'
import { paths } from '../Navbar/NavbarMenu/paths'
import { getInitials } from './getInitials'
import { getSwitchableProfiles, SwitchableProfile } from './switchableProfiles'

const MENU_WIDTH = 250
const AVATAR_SIZE = 36
const MENU_ICON_SIZE = 16

/**
 * Arc replacement for the legacy Radix profile dropdown: a Mantine Menu
 * triggered by the avatar, with the same content (identity, current school,
 * my profile, profile switching, admin links and logout) and side effects.
 */
export const UserMenu = () => {
  const { t } = useTranslation()
  const { currentUser } = useCurrentUser()
  const { avatarOverride } = useAvatarOverride()

  if (!currentUser?.id) {
    return <Skeleton height={AVATAR_SIZE} width={AVATAR_SIZE} circle />
  }

  const email = currentUser.email || currentUser.username
  const schoolName = currentUser.selected_school?.name
  const isGoogleConnected = Boolean(currentUser.google_refresh_token)
  const avatarUrl = avatarOverride !== null ? avatarOverride : currentUser.avatar_url || ''
  const switchableProfiles = getSwitchableProfiles(currentUser)

  const logOut = () => {
    window.location.replace('/account/signout')
  }

  const switchProfile = (profile: SwitchableProfile) => {
    if (profile.clearsCachedUser) window.localStorage.removeItem('current_user')
    window.location.href = paths.account.profileChange(profile.value)
  }

  return (
    <Menu shadow='md' width={MENU_WIDTH} position='bottom-end' withinPortal>
      <Menu.Target>
        <UnstyledButton
          aria-label={t('general_profiles_my')}
          sx={theme => ({
            display: 'flex',
            alignItems: 'center',
            height: 44,
            paddingLeft: 4,
            paddingRight: 4,
            borderRadius: 8,
            transition: 'background-color 150ms ease',
            '&:hover': {
              backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
            },
          })}
        >
          <Group spacing='xs' noWrap>
            <Avatar
              src={avatarUrl || null}
              alt={currentUser.full_name}
              radius='xl'
              size={AVATAR_SIZE}
            >
              {getInitials(currentUser.full_name || '')}
            </Avatar>
            <IconChevronDown size={MENU_ICON_SIZE} />
          </Group>
        </UnstyledButton>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>
          <Text size='sm' fw={700}>
            {currentUser.full_name}
          </Text>
          <Group spacing={4} noWrap>
            {email && (
              <Text size='xs' color='dimmed' lh={1.6}>
                {email}
              </Text>
            )}
            {isGoogleConnected && (
              <Tooltip label='Google connected' withArrow>
                <span style={{ display: 'inline-flex' }}>
                  <GoogleIcon />
                </span>
              </Tooltip>
            )}
          </Group>
        </Menu.Label>

        {schoolName && (
          <>
            <Menu.Divider />
            <Menu.Label>{t('school')}</Menu.Label>
            <Text size='sm' fw={500} lh={1.6} px={16} pb={8}>
              {schoolName}
            </Text>
          </>
        )}

        <Menu.Divider />
        {/* SPA route: router Link keeps the persistent shell mounted */}
        <Menu.Item
          icon={<IconUserEdit size={MENU_ICON_SIZE} />}
          component={Link}
          to='/app/profile'
        >
          {t('general_profiles_my')}
        </Menu.Item>

        {switchableProfiles.length >= 2 && (
          <>
            <Menu.Divider />
            <Menu.Label>{t('switch_profile')}</Menu.Label>
            {switchableProfiles.map(profile => (
              <Menu.Item
                key={profile.value}
                icon={
                  <IconCheck
                    size={MENU_ICON_SIZE}
                    style={{ opacity: currentUser.profile_used === profile.value ? 1 : 0 }}
                  />
                }
                onClick={() => switchProfile(profile)}
              >
                {t(profile.labelKey)}
              </Menu.Item>
            ))}
          </>
        )}

        {currentUser.admin_account && (
          <>
            <Menu.Divider />
            <Menu.Label>Admin area</Menu.Label>
            <Menu.Item component='a' href={paths.account.admin.curriculums}>
              {t('curriculum')}
            </Menu.Item>
            <Menu.Item component='a' href={paths.account.admin.school}>
              {t('school')}
            </Menu.Item>
            <Menu.Item component='a' href={paths.account.admin.transfer_students}>
              {t('transfer_students')}
            </Menu.Item>
            {/* Feature flags is the one admin screen owned by the SPA router */}
            <Menu.Item component={Link} to={paths.account.admin.feature_flags}>
              {t('feature_flags')}
            </Menu.Item>
          </>
        )}

        <Menu.Divider />
        <Menu.Item color='red' icon={<IconLogout size={MENU_ICON_SIZE} />} onClick={logOut}>
          {t('logout')}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  )
}
